import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { useLocation, useNavigate } from 'react-router'
// auth
import { getAccessToken, clearAccessToken } from 'lib/auth'
// store
import { store } from 'store/store'
import { LOGOUT_ACTION } from 'store/types'
// graphql
import { AUTH_REFRESH_TOKEN_MUTATION } from 'graphql/auth/mutations'
// hooks
import { useShowModal } from 'hooks/useShowModal'
import { useCurrentUser } from 'hooks/useCurrentUser'
// constants
import { Modals } from 'constants/modals'
import { isPublicPage as isPublic } from 'constants/security'
import { AuthChannelEvent, ACTIVITY_EVENTS, INACTIVITY_TIMEOUT_MS, INACTIVITY_WARNING_MS, DEFAULT_ABSOLUTE_TTL_SECONDS } from './constants'
// core
import { SessionManager } from './SessionManager'
// types
import type { RefreshTokenResponse } from './types'

const WARNING_BEFORE_TIMEOUT_MS = 2 * 60 * 1000

export const useSessionManager = (authChannel: BroadcastChannel | null) => {
  const navigate = useNavigate()

  const { pathname } = useLocation()

  const isPublicPage = isPublic(pathname)

  const user = useCurrentUser()

  const idleTimeoutSeconds = user?.idleTimeoutSeconds ?? user?.company?.idleTimeoutSeconds
  const inactivityTimeoutMs = idleTimeoutSeconds ? idleTimeoutSeconds * 1000 : INACTIVITY_TIMEOUT_MS
  const warningTimeoutMs = idleTimeoutSeconds ? Math.max(inactivityTimeoutMs - WARNING_BEFORE_TIMEOUT_MS, 0) : INACTIVITY_WARNING_MS

  const [refreshMutation] = useMutation<RefreshTokenResponse>(AUTH_REFRESH_TOKEN_MUTATION, { context: { handledLocally: true } })

  const { showModal: showSessionExpiringModal, hideModal: hideSessionExpiringModal } = useShowModal({
    modalType: Modals.SESSION_EXPIRING_MODAL
  })

  // Created once via lazy initializer — identity never changes
  const [manager] = React.useState(
    () =>
      new SessionManager({
        refreshMutation,
        showSessionExpiringModal,
        hideSessionExpiringModal,
        authChannel,
        routerPush: navigate,
        dispatchLogout: () => store.dispatch({ type: LOGOUT_ACTION }),
        getAbsoluteTtlMs: () => {
          const user = (store.getState() as any).currentUser?.data
          const ttlSeconds = user?.refreshTokenAbsoluteTtl ?? user?.company?.refreshTokenAbsoluteTtl ?? DEFAULT_ABSOLUTE_TTL_SECONDS

          return ttlSeconds * 1000
        },
        onNetworkRecovered: () => {
          window.dispatchEvent(new CustomEvent('skefto-network-recovered'))
        }
      })
  )

  // React deps (mutations, router, etc.) change on every render — keep SessionManager in sync
  React.useEffect(() => {
    manager.updateDeps({
      refreshMutation,
      showSessionExpiringModal,
      hideSessionExpiringModal,
      authChannel,
      routerPush: navigate,
      dispatchLogout: () => store.dispatch({ type: LOGOUT_ACTION }),
      inactivityTimeoutMs,
      warningTimeoutMs,
      getAbsoluteTtlMs: () => {
        const u = (store.getState() as any).currentUser?.data
        const ttlSeconds = u?.refreshTokenAbsoluteTtl ?? u?.company?.refreshTokenAbsoluteTtl ?? DEFAULT_ABSOLUTE_TTL_SECONDS

        return ttlSeconds * 1000
      },
      onNetworkRecovered: () => {
        window.dispatchEvent(new CustomEvent('skefto-network-recovered'))
      }
    })
  })

  // Cross-tab sync via BroadcastChannel
  React.useEffect(() => {
    if (!authChannel) {
      return
    }

    const handleMessage = (event: MessageEvent) => {
      switch (event.data.type) {
        case AuthChannelEvent.TOKEN_REFRESHED:
          manager.syncFromOtherTab(event.data.accessToken)
          break

        case AuthChannelEvent.LOGOUT:
          manager.clearAllTimers()
          clearAccessToken()
          store.dispatch({ type: LOGOUT_ACTION })
          navigate('/login')
          break

        case AuthChannelEvent.ACTIVITY:
          manager.resetInactivity(true)
          break
      }
    }

    authChannel.addEventListener('message', handleMessage)

    return () => {
      authChannel.removeEventListener('message', handleMessage)
    }
  }, [authChannel, manager])

  // Network awareness
  React.useEffect(() => {
    if (isPublicPage) return

    const handleOnline = () => manager.goOnline()
    const handleOffline = () => manager.goOffline()

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [isPublicPage, manager])

  // Track user activity via DOM events
  React.useEffect(() => {
    if (isPublicPage) {
      return
    }

    const onActivity = () => {
      manager.handleActivity()
    }

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true })
    })

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity)
      })
    }
  }, [isPublicPage, manager])

  // Bootstrap session on mount
  React.useEffect(() => {
    if (isPublicPage) {
      return
    }

    const token = getAccessToken()

    if (token) {
      manager.init(token)
    }

    return () => {
      manager.clearAllTimers()
    }
  }, [isPublicPage, manager])

  // Restart inactivity timers when user settings load after init()
  React.useEffect(() => {
    if (isPublicPage) return

    manager.restartInactivity()
  }, [inactivityTimeoutMs, warningTimeoutMs])
}
